import { z } from 'zod'
import { cornerRadiusSchema } from '../enums/corner-radius.enum.js'
import { fontFamilySchema } from '../enums/font-family.enum.js'
import { PAPER_COPY_FEE_MAX_MICROS } from '../constants/envelope.js'
import { emailSchema } from '../validators/email.validator.js'
import {
  hexColorSchema,
  httpsUrlSchema,
  tenantSlugSchema,
  timeZoneSchema,
} from '../validators/tenant.validator.js'

/** Name, sender identity, and look: what staff (and later owners) can edit. */
export const tenantProfileInputSchema = z
  .object({
    name: z.string().trim().min(1, 'Required').max(200),
    displayName: z.string().trim().min(1, 'Required').max(200),
    fromName: z.string().trim().min(1, 'Required').max(200),
    replyTo: emailSchema,
    logoUrl: httpsUrlSchema.nullable(),
    accentColor: hexColorSchema.nullable(),
    /** Signer pages and signer email: `medium` and `inter` are PixelSign's own look. */
    cornerRadius: cornerRadiusSchema,
    fontFamily: fontFamilySchema,
    timezone: timeZoneSchema,
    /**
     * What the organization charges a signer for a paper copy, in whole cents
     * as micros; 0 is free. The consent tells signers (docs/consent-and-paper-copies.md).
     */
    paperCopyFeeMicros: z
      .number()
      .int()
      .min(0)
      .max(PAPER_COPY_FEE_MAX_MICROS, 'At most $1,000')
      .refine((micros) => micros % 10_000 === 0, 'Whole cents only'),
  })
  .strict()

export type TenantProfileInput = z.infer<typeof tenantProfileInputSchema>

function tenantProfileFields<const T extends readonly (keyof TenantProfileInput)[]>(
  fields: T & (Exclude<keyof TenantProfileInput, T[number]> extends never ? T : never),
): T {
  return fields
}

/**
 * Every field of {@link TenantProfileInput}. The repository's change log and
 * the MCP profile tools walk this list, so a new field has to be named here
 * or the list stops compiling.
 */
export const TENANT_PROFILE_FIELDS = tenantProfileFields([
  'name',
  'displayName',
  'fromName',
  'replyTo',
  'logoUrl',
  'accentColor',
  'cornerRadius',
  'fontFamily',
  'timezone',
  'paperCopyFeeMicros',
])

/** Staff onboarding: the profile, a permanent slug, and who to invite as owner. */
export const createTenantInputSchema = tenantProfileInputSchema
  .extend({
    slug: tenantSlugSchema,
    ownerEmail: emailSchema,
  })
  .strict()

export type CreateTenantInput = z.infer<typeof createTenantInputSchema>

export const suspendTenantInputSchema = z
  .object({ reason: z.string().trim().min(1, 'Give a reason').max(500) })
  .strict()

export type SuspendTenantInput = z.infer<typeof suspendTenantInputSchema>

/** Closing an organization for good; the reason goes in its activity log and on the ops console. */
export const closeTenantInputSchema = z
  .object({ reason: z.string().trim().min(1, 'Give a reason').max(500) })
  .strict()

export type CloseTenantInput = z.infer<typeof closeTenantInputSchema>
